import { ReviewTextField, mergeFieldSx } from './FieldReview';
import { SxProps, Theme } from '@mui/material/styles';
import { get } from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CustomField = ({ options, stateProps, children, sx, }: CustomFieldProps) => {
  const { t } = useTranslation();

  const label = t(options.label ?? options.name.charAt(0).toUpperCase() + options.name.slice(1));

  const onChangeFunction = options.handleChange ?? (
    stateProps?.setState != undefined
      ? (event: React.ChangeEvent<HTMLInputElement>) => {
        stateProps?.setState?.(event.target.value);
      }
      : stateProps?.handleChange);

  let value = stateProps?.state ?? get(stateProps?.values ?? {}, options.name, '');
  if (
    options.select &&
    !options.isMulti &&
    (typeof value === 'object' || value === undefined)
  ) {
    value = '';
  }

  const [selectOpen, setSelectOpen] = useState(false);
  useEffect(() => {
    if (options.selectOpen !== undefined) {
      setSelectOpen(options.selectOpen);
    }
  }, [options.selectOpen]);

  const handleSelectOpen = () => {
    setSelectOpen(true);
    if (typeof options?.setSelectOpen === 'function') {
      options?.setSelectOpen(true);
    }
  };
  const handleSelectClose = () => {
    setSelectOpen(false);
    if (typeof options?.setSelectOpen === 'function') {
      options?.setSelectOpen(false);
    }
  };

  const isTouched = get(stateProps?.touched, options.name);
  const error = get(stateProps?.errors, options.name);
  const hasSubmitted = (stateProps?.submitCount ?? 0) > 0;

  const showError = (isTouched || hasSubmitted) && error;

  // --- generic warning logic ---
  const values = stateProps?.values ?? {};
  const isEmpty = value === '' || value === null || typeof value === 'undefined';

  let warningText = '';

  if (!showError && options.warning && (isTouched || hasSubmitted)) {
    if (typeof options.warning === 'string') {
      // only warn when empty
      if (isEmpty) {
        warningText = options.warning;
      }
    } else if (typeof options.warning === 'function') {
      warningText = options.warning({ value, values }) ?? '';
    }
  }

  // --- info from tooltip when FormInfoButton is active ---
  const status = stateProps?.status ?? {};
  const showAllTooltips = Boolean(status.showAllTooltips);
  const tooltipText = options.tooltipText;

  const showTooltipInfo = showAllTooltips && !!tooltipText && !showError && !warningText;

  const helperText = showError ? error : warningText || (showTooltipInfo ? tooltipText : '');

  const isError = Boolean(showError);
  const isWarning = !isError && Boolean(warningText);
  const isInfo = !isError && !isWarning && showTooltipInfo;

  const baseSx: SxProps<Theme> = {
    '&.MuiFormControl-root': { marginTop: options?.topMargin ?? '16px', },
    '& .MuiOutlinedInput-root': { height: options?.height == null ? 'auto' : options.height + '  !important', },
    '& label.MuiInputLabel-root': { marginTop: options?.height == null ? '0' : '-5px  !important', },
    // Yellow helper text if warning
    ...(isWarning && { '& .MuiFormHelperText-root': { color: 'warning.main', }, }),
    // Blue helper text if info (tooltip)
    ...(isInfo && { '& .MuiFormHelperText-root': { color: 'info.main', }, }),
  };

  return (
    <ReviewTextField label={label} name={options.name} value={value} className={options.className ?? 'custom-field-' + label?.toLowerCase().replace(' ', '-')} onBlur={stateProps?.handleBlur} multiline={options.multiLine ?? false} rows={options.rows} maxRows={options.maxRows} placeholder={options.placeholder} onChange={onChangeFunction} InputProps={{ readOnly: options.disabled }} inputProps={{ readOnly: options.disabled }} select={options.select ?? false} SelectProps={{ multiple: options.isMulti ?? false, open: selectOpen, onOpen: handleSelectOpen, onClose: handleSelectClose, }} helperText={helperText} error={isError} type={options.type ?? 'text'} variant="outlined" fullWidth sx={mergeFieldSx(baseSx, sx)} margin="normal">{children}</ReviewTextField>
  );
};

interface CustomFieldProps {
  stateProps?: {
    values?: any;
    handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    touched?: any;
    errors?: any;
    handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    state?: any;
    setState?: React.Dispatch<React.SetStateAction<any>>;
    submitCount?: number;
    status?: any;
  };
  options: {
    label?: string;
    handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    multiLine?: boolean;
    select?: boolean;
    isMulti?: boolean;
    type?: string;
    name: string;
    height?: string;
    topMargin?: string;
    selectOpen?: boolean;
    className?: string;
    setSelectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    // Multiline extras
    rows?: number;
    maxRows?: number;
    placeholder?: string;

    // generic warning support
    warning?: string | ((ctx: { value: any; values: any }) => string | undefined);

    // NEW: tooltip text from FormField
    tooltipText?: ReactNode;
  };
  children?: ReactNode;
  sx?: SxProps<Theme>;
}
